/**
 * GMA-1627 (CompassCare Web 12/14 — Messages) — pure messages model.
 *
 * Message-template catalog, broadcast-cohort resolution, and family visibility
 * permission-gating. Privacy-sensitive gating fails CLOSED: an unknown role or
 * malformed input returns false (never accidentally exposes a thread).
 *
 * All functions fail safe: bad input yields a benign result, never a throw.
 */

export const MESSAGE_TEMPLATES = Object.freeze({
  wellness_check: Object.freeze({ label: "Wellness check request" }),
  safety_message: Object.freeze({ label: "Safety message" }),
  hydration_reminder: Object.freeze({ label: "Hydration reminder" }),
  shift_handoff: Object.freeze({ label: "Shift handoff note" }),
});

export const PARTICIPANT_ROLES = Object.freeze([
  "caregiver",
  "care_team",
  "family",
  "dispatch",
]);

// A valid id is a non-empty string that is ALREADY normalized (no surrounding
// whitespace) — this module is privacy-sensitive and fails closed on anything
// malformed, so " a " must not match "a" nor slip past a permission check
// (Copilot #306).
const isNormalizedId = (v) =>
  typeof v === "string" && v.length > 0 && v === v.trim();

// Only valid normalized ids — drops null/undefined/""/" "/" a " so a
// malformed id can't accidentally match (or be matched by) another.
const asClientIds = (v) => (Array.isArray(v) ? v.filter(isNormalizedId) : []);

/**
 * Resolve the caregiver recipients for a broadcast to a client cohort (e.g.
 * "all caregivers of heat-advisory-impacted clients"). A caregiver is included
 * when any of their clients is in the cohort. Deduped by id, order preserved.
 * @param {Array<{id, clientIds}>} caregivers
 * @param {{clientIds: string[]}} cohort
 * @returns {Array} matching caregivers ([] on bad/empty input)
 */
export function resolveBroadcastRecipients(caregivers, cohort) {
  const list = Array.isArray(caregivers) ? caregivers : [];
  const cohortIds = new Set(asClientIds(cohort && cohort.clientIds));
  if (cohortIds.size === 0) return [];
  const seen = new Set();
  const out = [];
  for (const cg of list) {
    if (!cg || !isNormalizedId(cg.id) || seen.has(cg.id)) continue;
    if (asClientIds(cg.clientIds).some((id) => cohortIds.has(id))) {
      seen.add(cg.id);
      out.push(cg);
    }
  }
  return out;
}

/**
 * Whether a participant may see a thread. Family visibility is permission-gated
 * and fails CLOSED.
 *   - family: only their own client's thread AND only when familyVisible.
 *   - caregiver: only their own clients' threads.
 *   - care_team / dispatch: any thread that names a valid client (threads
 *     with a missing/malformed clientId fail closed for EVERY role).
 *   - anything else (unknown role / malformed input): false.
 * @param {{role, clientIds?}} participant
 * @param {{clientId, familyVisible?}} thread
 * @returns {boolean}
 */
export function canSeeThread(participant, thread) {
  if (!participant || typeof participant !== "object") return false;
  if (!thread || typeof thread !== "object") return false;
  const clientId = thread.clientId;
  // A client-scoped thread must name a real, NORMALIZED client id — fail
  // closed for ALL roles (incl. care_team/dispatch) when it doesn't.
  if (!isNormalizedId(clientId)) return false;
  const role = participant.role;
  const owns = asClientIds(participant.clientIds).includes(clientId);
  switch (role) {
    case "care_team":
    case "dispatch":
      return true;
    case "caregiver":
      return owns;
    case "family":
      return owns && thread.familyVisible === true;
    default:
      return false; // fail closed
  }
}
